import { css, type Theme } from "@emotion/react";
import styled from "@emotion/styled";
import { Chip } from "@mui/material";
import type { ComponentProps } from "react";

import { shade } from "@skilitics/designer-theme";

import { FONT_WEIGHT_MEDIUM, transientOptions } from "../utilities";

type ChipType = "primary" | "secondary" | "tertiary" | "neutral" | "general" | "warning";

const chipColorStyles = (type: ChipType, hasClickHandler: boolean, theme: Theme) => {
  switch (type) {
    case "primary":
      return css`
        color: ${theme.primaryStrong};
        background: ${theme.primaryContainer};
        ${hasClickHandler &&
        `
        &:hover,
        &:focus {
          background: ${shade(theme.primaryContainer, 0.04)};
        }`}
      `;
    case "secondary":
      return css`
        color: ${theme.secondaryStrong};
        background: ${theme.secondaryContainer};
        ${hasClickHandler &&
        `
        &:hover,
        &:focus {
          background: ${shade(theme.secondaryContainer, 0.04)};
        }`}
      `;
    case "tertiary":
      return css`
        color: ${theme.tertiaryStrong};
        background: ${theme.tertiaryContainer};
        ${hasClickHandler &&
        `
        &:hover,
        &:focus {
          background: ${shade(theme.tertiaryContainer, 0.04)};
        }`}
      `;
    case "neutral":
      return css`
        color: ${theme.onNeutral};
        background: ${theme.neutral};
        ${hasClickHandler &&
        `
        &:hover,
        &:focus {
          background: ${shade(theme.neutral, 0.04)};
        }`}
      `;
    case "general":
      return css`
        color: ${theme.generalTextIcon};
        background: ${theme.surfaceSoft};
        ${hasClickHandler &&
        `
        &:hover,
        &:focus {
          background: ${shade(theme.surfaceSoft, 0.04)};
        }`}
      `;
    case "warning":
      return css`
        color: ${theme.warning};
        background: ${theme.warningContainer};
        ${hasClickHandler &&
        `
        &:hover,
        &:focus {
          background: ${shade(theme.warningContainer, 0.04)};
        }`}
      `;
  }
};
const StyledChip = styled(Chip, transientOptions)<{ $type: ChipType; $hasClickHandler: boolean }>`
  text-transform: uppercase;
  font-size: ${({ theme }) => theme.fontSizeSmall};
  font-weight: ${FONT_WEIGHT_MEDIUM};
  height: 20px;
  margin-right: 5px;
  user-select: none;
  > span {
    display: block;
    padding: 4px 6px;
  }
  ${({ $type, $hasClickHandler, theme }) => chipColorStyles($type, $hasClickHandler, theme)};
`;

type PrefixChipProps = ComponentProps<typeof Chip> & {
  type: ChipType;
};

export const PrefixChip = ({ type, onClick, ...rest }: PrefixChipProps) => {
  return <StyledChip $type={type} $hasClickHandler={!!onClick} onClick={onClick} {...rest} />;
};
